import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "recurringFrequencyWrapper",
    "recurringFrequencyField",
    "recurringPeriodWrapper",
    "recurringPeriodField",
    "sendEmailField",
    "submitButton",
    "externalLinkWrapper",
    "simulatedLiveWrapper",
    "eventLinkField",
    "videoField",
    "videoDurationField",
    "videoStatusLabel",
    "eventDateField",
    "eventTimeField",
    "endsAtField",
    "endsAtWrapper",
    "endsAtLabel",
    "endsAtHint",
    "timeSlotsControls",
    "additionalTimeSlots",
    "additionalTimeSlot",
    "timeSlotTemplate",
    "addTimeSlotButton",
    "scheduleActionsRow",
    "deliveryModeExternal",
    "deliveryModeSimulated",
  ];

  connect() {
    this.toggleDeliveryMode();
    if (this.hasSendEmailFieldTarget && this.hasSubmitButtonTarget) {
      this.updateConfirmMessage();
    }
    this.refreshEndPreview();
    this.refreshAdditionalSlotEnds();
  }

  removeSpaces(event) {
    let field = event.target;
    field.value = field.value.replace(/\s/g, "");
  }

  show_recurrance_options(event) {
    if (event.target.checked) {
      this.recurringFrequencyWrapperTarget.classList.remove("d-none");
      this.recurringPeriodWrapperTarget.classList.remove("d-none");
      this.recurringFrequencyFieldTarget.setAttribute("required", "required");
      this.recurringPeriodFieldTarget.setAttribute("required", "required");
    } else {
      this.recurringFrequencyWrapperTarget.classList.add("d-none");
      this.recurringPeriodWrapperTarget.classList.add("d-none");
      this.recurringFrequencyFieldTarget.removeAttribute("required");
      this.recurringPeriodFieldTarget.removeAttribute("required");
    }
  }

  updateConfirmMessage() {
    if (!this.hasSendEmailFieldTarget || !this.hasSubmitButtonTarget) return;
    const isChecked = this.sendEmailFieldTarget.checked;
    if (isChecked) {
      this.submitButtonTarget.dataset.confirm =
        "You are about to send an email to everyone in your Unsolved Network. Are you sure?";
    } else {
      this.submitButtonTarget.dataset.confirm =
        "Are you sure you want to publish this event?";
    }
  }

  simulatedLiveSelected() {
    if (this.hasDeliveryModeSimulatedTarget) {
      return this.deliveryModeSimulatedTarget.checked;
    }
    return false;
  }

  toggleDeliveryMode() {
    const jll = this.simulatedLiveSelected();

    if (this.hasExternalLinkWrapperTarget) {
      this.externalLinkWrapperTarget.classList.toggle("d-none", jll);
    }
    if (this.hasSimulatedLiveWrapperTarget) {
      this.simulatedLiveWrapperTarget.classList.toggle("d-none", !jll);
    }
    if (this.hasTimeSlotsControlsTarget) {
      this.timeSlotsControlsTarget.classList.toggle("d-none", !jll);
    }
    if (this.hasAddTimeSlotButtonTarget) {
      this.addTimeSlotButtonTarget.classList.toggle("d-none", !jll);
    }

    // Disable hidden fields so HTML5 constraint validation cannot block submit
    // (Chrome silently refuses submit when a required/invalid field is display:none).
    if (this.hasEventLinkFieldTarget) {
      this.eventLinkFieldTarget.disabled = !!jll;
      if (jll) {
        this.eventLinkFieldTarget.removeAttribute("required");
      } else {
        this.eventLinkFieldTarget.setAttribute("required", "required");
      }
    }

    if (this.hasEndsAtFieldTarget) {
      // JLL: readonly calculated end (still submits). Meeting link: editable required end.
      this.endsAtFieldTarget.readOnly = !!jll;
      this.endsAtFieldTarget.disabled = false;
      if (jll) {
        this.endsAtFieldTarget.removeAttribute("required");
      } else {
        this.endsAtFieldTarget.setAttribute("required", "required");
      }
    }

    if (this.hasEndsAtLabelTarget) {
      this.endsAtLabelTarget.textContent = jll ? "End Time (Calculated)" : "End time";
    }
    if (this.hasEndsAtHintTarget) {
      this.endsAtHintTarget.classList.toggle("d-none", jll);
    }

    if (this.hasVideoFieldTarget) {
      this.videoFieldTarget.disabled = !jll;
    }

    if (this.hasVideoDurationFieldTarget) {
      this.videoDurationFieldTarget.disabled = !jll;
    }

    if (!jll && this.hasAdditionalTimeSlotsTarget) {
      this.additionalTimeSlotsTarget.innerHTML = "";
    }

    this.refreshEndPreview();
    this.refreshAdditionalSlotEnds();
  }

  onVideoSelected(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      this.resetVideoStatusLabel();
      return;
    }

    if (this.hasVideoStatusLabelTarget) {
      this.videoStatusLabelTarget.textContent = file.name;
    }

    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = url;
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const duration = Math.round(video.duration || 0);
      if (this.hasVideoDurationFieldTarget) {
        this.videoDurationFieldTarget.value = duration > 0 ? duration : "";
      }
      if (this.hasVideoStatusLabelTarget && duration > 0) {
        this.videoStatusLabelTarget.textContent = `${this.formatDuration(
          duration
        )} recording selected`;
      }
      this.refreshEndPreview();
      this.refreshAdditionalSlotEnds();
    };
  }

  resetVideoStatusLabel() {
    if (!this.hasVideoStatusLabelTarget) return;
    const attachedLabel = this.videoStatusLabelTarget.dataset.attachedLabel;
    this.videoStatusLabelTarget.textContent = attachedLabel || "No file chosen";
  }

  refreshEndPreview() {
    if (!this.hasEndsAtFieldTarget) return;
    if (!this.simulatedLiveSelected()) return;

    const duration = this.currentDurationSeconds();
    const date = this.hasEventDateFieldTarget ? this.eventDateFieldTarget.value : "";
    const time = this.hasEventTimeFieldTarget ? this.eventTimeFieldTarget.value : "";

    const endValue = this.calculateEndLocalValue(date, time, duration);
    if (endValue) {
      this.endsAtFieldTarget.value = endValue;
    }
  }

  refreshAdditionalSlotEnds() {
    if (!this.hasAdditionalTimeSlotsTarget) return;
    const duration = this.currentDurationSeconds();
    this.additionalTimeSlotsTarget
      .querySelectorAll("[data-events-target='additionalTimeSlot']")
      .forEach((row) => {
        const dateInput = row.querySelector("[data-slot-date]");
        const timeInput = row.querySelector("[data-slot-time]");
        const endInput = row.querySelector("[data-slot-end]");
        if (!dateInput || !timeInput || !endInput) return;
        const endValue = this.calculateEndLocalValue(
          dateInput.value,
          timeInput.value,
          duration
        );
        endInput.value = endValue || "";
      });
  }

  addTimeSlot(event) {
    event.preventDefault();
    if (!this.simulatedLiveSelected()) return;
    if (!this.hasTimeSlotTemplateTarget || !this.hasAdditionalTimeSlotsTarget) return;

    const node = this.timeSlotTemplateTarget.content.cloneNode(true);
    this.additionalTimeSlotsTarget.appendChild(node);
    this.refreshAdditionalSlotEnds();
  }

  removeTimeSlot(event) {
    event.preventDefault();
    const row = event.target.closest("[data-events-target='additionalTimeSlot']");
    if (row) row.remove();
  }

  currentDurationSeconds() {
    if (!this.hasVideoDurationFieldTarget) return NaN;
    return parseInt(this.videoDurationFieldTarget.value, 10);
  }

  calculateEndLocalValue(date, time, durationSeconds) {
    if (!durationSeconds || durationSeconds <= 0 || !date || !time) return null;
    const start = new Date(`${date}T${time}`);
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + durationSeconds * 1000);
    return this.toDatetimeLocalValue(end);
  }

  toDatetimeLocalValue(date) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  formatDuration(seconds) {
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  }
}
